import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Award, Heart, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

export default function About() {
  return (
    <div className="bg-brand-base min-h-screen">
      <Navbar />

      {/* Hero */}
      <section data-testid="about-hero" className="relative min-h-[60vh] flex items-end overflow-hidden pt-24">
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ background: "radial-gradient(circle at 30% 50%, rgba(139,0,255,0.1) 0%, transparent 60%)" }} className="absolute inset-0" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-16 w-full relative">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Poznaj artystkę</p>
            <h1 className="font-anton text-6xl sm:text-8xl md:text-[10vw] text-white uppercase leading-none">
              O<br /><span className="gradient-text">MNIE</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main story */}
      <section className="py-20 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-start">
          <FadeUp>
            <div className="relative sticky top-28">
              <img src="./images/IMG_3793.JPG"
                alt="Milena Bachara" className="w-full object-cover" />
              <div className="absolute inset-0 border border-violet-600/15" />
              <div className="absolute -top-6 -left-6 w-32 h-32 border border-violet-600/20" />
              <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-violet-600/5 border border-violet-600/15" />
              {/* Name card */}
              <div className="absolute bottom-8 left-8 right-8 p-6 bg-black/80 backdrop-blur-xl border border-white/5">
                <div className="font-anton text-2xl text-white uppercase">Milena Bachara</div>
                <div className="text-violet-400 text-xs uppercase tracking-[0.2em] mt-1">Fryzjer Artysta / Kolorystka</div>
              </div>
            </div>
          </FadeUp>

          <div className="space-y-12">
            <FadeUp delay={0.1}>
              <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-6">Historia</p>
              <h2 className="font-anton text-4xl md:text-5xl text-white uppercase leading-tight mb-6">
                KAŻDE WŁOSY<br />TO NOWE <span className="gradient-text">WYZWANIE</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed">
                Zaczęłam swoją przygodę z fryzjerstwem z jedną myślą - że włosy mogą być czymś więcej niż tylko fryzurą. Mogą być wyrazem osobowości, sztuki, odwagi i kobiecości jednocześnie.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <p className="text-white/60 text-base leading-relaxed">
                Specjalizuję się w koloryzacji - od klasycznych technik po najbardziej kreatywne i awangardowe projekty kolorystyczne. Każda metamorfoza to dla mnie projekt artystyczny, który wymaga skupienia, pasji i precyzji.
              </p>
            </FadeUp>

            <FadeUp delay={0.3}>
              <p className="text-white/60 text-base leading-relaxed">
                Pracuję solo, bo wierzę, że każda klientka zasługuje na 100% mojej uwagi. Nie musisz czekać na swoją kolejkę - od pierwszej chwili jestem tylko dla Ciebie.
              </p>
            </FadeUp>

            {/* Values */}
            <FadeUp delay={0.4}>
              <h3 className="font-outfit text-xs uppercase tracking-[0.3em] text-white/40 mb-6">Moje wartości</h3>
              <div className="space-y-4">
                {[
                  { icon: <Zap size={16} />, title: "Artyzm", desc: "Każda koloryzacja to dzieło - przemyślane i niepowtarzalne." },
                  { icon: <Award size={16} />, title: "Precyzja", desc: "Jakość produktów i techniki na poziomie światowym." },
                  { icon: <Heart size={16} />, title: "Troska", desc: "Słucham i rozumiem Ciebie - nie szablonu." },
                ].map((v, i) => (
                  <div key={i} className="flex gap-4 p-6 border border-white/5 card-hover">
                    <div className="text-violet-500 mt-0.5">{v.icon}</div>
                    <div>
                      <div className="font-outfit font-semibold text-white mb-1">{v.title}</div>
                      <div className="text-white/50 text-sm">{v.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.5}>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/rezerwacja" data-testid="about-book-btn" className="btn-primary justify-center">
                  Zarezerwuj <ArrowRight size={14} />
                </Link>
                <Link to="/szkolenia" data-testid="about-trainings-btn" className="btn-secondary justify-center">
                  Szkolenia
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Philosophy quote */}
      <section data-testid="about-philosophy" className="py-24 relative" style={{ background: "#0A0A0A" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(139,0,255,0.08) 0%, transparent 70%)" }} />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <FadeUp>
            <div className="section-marker mx-auto mb-8" />
            <blockquote className="font-anton text-3xl md:text-4xl text-white uppercase leading-tight">
              "WŁOSY SĄ JAK PŁÓTNO - <span className="gradient-text">A JA MALARKĄ."</span>
            </blockquote>
            <div className="text-white/30 text-sm mt-6 uppercase tracking-[0.2em]">- Milena Bachara</div>
          </FadeUp>
        </div>
      </section>

      {/* Specializations */}
      <section data-testid="about-specializations" className="py-20 max-w-screen-2xl mx-auto px-6 md:px-12">
        <FadeUp>
          <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Specjalizacje</p>
          <h2 className="font-anton text-4xl text-white uppercase mb-12">CZYM SIĘ ZAJMUJĘ</h2>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", title: "Zaawansowana Koloryzacja", desc: "Techniki premium - balayage, ombre, babylights, koloryzacja globalna dobrana pod Ciebie." },
            { n: "02", title: "Kolory Kreatywne", desc: "Odważne fiolety, błękity, róże i pastele. Sztuka na włosach dla tych, którzy nie boją się wyróżniać." },
            { n: "03", title: "Szkolenia Kolorystyczne", desc: "Dzielę się wiedzą z innymi fryzjerami. Masterclassy i warsztaty dla profesjonalistów." },
          ].map((s, i) => (
            <FadeUp key={i} delay={i * 0.15}>
              <div className="border-t border-white/10 pt-8">
                <div className="font-anton text-6xl text-stroke mb-6 leading-none">{s.n}</div>
                <h3 className="font-anton text-2xl text-white uppercase mb-4">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
